/**
 * SceneRenderer draws a theme onto a canvas: cached static layers, live
 * animated layers with parallax, ambient particles, a gentle tension tint
 * and an ~800 ms crossfade when the theme changes.
 *
 * It owns no timing policy — engine.ts decides when to draw (animation loop,
 * pause, reduced motion). With animation off it paints one static frame
 * straight onto the canvas and keeps no offscreen bitmaps at all.
 */
import type { ThemePack } from '@dropstack/core';
import { backingRatio, clamp, parallaxOffset, sceneSize, smoothstep, type SceneSize } from './layout.ts';
import { paintLiveLayer, paintParticles, paintScene, paintStaticLayers } from './paintScene.ts';
import { rgba } from './painters/draw.ts';
import { layerExtent } from './painters/index.ts';
import { planLayers, planPasses, type PlannedLayer, type ScenePass } from './plan.ts';

/** Theme crossfade duration. */
export const CROSSFADE_MS = 800;

/** A static pass baked into an offscreen canvas covering rows [top, bottom) of the viewport (CSS px). */
interface Bitmap {
  canvas: HTMLCanvasElement;
  top: number;
  bottom: number;
}

type CompiledPass = { kind: 'static'; depth: number; bitmap: Bitmap } | Extract<ScenePass, { kind: 'live' }>;

interface Fade {
  snapshot: HTMLCanvasElement;
  /** Timestamp of the first frame drawn after the change (set lazily, so a slow bake doesn't eat the fade). */
  start: number | null;
}

export class SceneRenderer {
  private readonly ctx: CanvasRenderingContext2D | null;
  private theme: ThemePack | null = null;
  private animate = true;
  private size: SceneSize = sceneSize(1, 1);
  private ratio = 1;
  /** Compiled passes for (theme, size, ratio); null when they need rebuilding. */
  private passes: CompiledPass[] | null = null;
  private fade: Fade | null = null;
  private drawn = false;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d');
  }

  /** Whether anything has been drawn at the current size. */
  get hasFrame(): boolean {
    return this.drawn;
  }

  get isFading(): boolean {
    return this.fade !== null;
  }

  /** Animation on: cached bitmaps + live layers + parallax. Off: single static frames. */
  setAnimate(animate: boolean): void {
    if (animate === this.animate) return;
    this.animate = animate;
    this.invalidate();
    if (!animate) this.fade = null;
  }

  /** Switch theme; with `crossfade`, the current picture fades out over CROSSFADE_MS. */
  setTheme(theme: ThemePack, crossfade: boolean): void {
    if (theme === this.theme) return;
    this.fade = crossfade && this.drawn ? { snapshot: this.snapshot(), start: null } : null;
    this.theme = theme;
    this.invalidate();
  }

  /** Match the canvas backing store to the viewport (CSS px) and device pixel ratio. */
  resize(width: number, height: number, devicePixelRatio: number | undefined): void {
    const ratio = backingRatio(width, height, devicePixelRatio);
    const w = Math.max(1, Math.round(width * ratio));
    const h = Math.max(1, Math.round(height * ratio));
    if (this.canvas.width === w && this.canvas.height === h && ratio === this.ratio && this.size.width === width && this.size.height === height) return;
    this.size = sceneSize(width, height);
    this.ratio = ratio;
    this.canvas.width = w;
    this.canvas.height = h;
    this.drawn = false;
    this.invalidate();
  }

  /** Drop any crossfade in progress (e.g. when the loop stops mid-fade). */
  finishFade(): void {
    this.fade = null;
  }

  /**
   * Draw one frame. `time` is scene time in seconds (drives motion), `now` a
   * wall-clock timestamp in ms (drives the crossfade), `intensity` 0–1 the
   * stack tension. Returns true when this frame also had to (re)bake the
   * cached layers — a one-off cost the frame governor should not count.
   */
  render(time: number, now: number, intensity = 0): boolean {
    const ctx = this.ctx;
    const theme = this.theme;
    if (!ctx || !theme) return false;
    const { size } = this;
    let baked = false;
    ctx.setTransform(this.ratio, 0, 0, this.ratio, 0, 0);
    ctx.globalAlpha = 1;
    if (!this.animate) {
      paintScene(ctx, theme, size, { time: 0 });
    } else {
      if (!this.passes) {
        this.passes = this.compile(theme);
        baked = true;
      }
      for (const pass of this.passes) {
        const dx = parallaxOffset(time, pass.depth, size);
        if (pass.kind === 'static') {
          const b = pass.bitmap;
          ctx.drawImage(b.canvas, dx - size.bleed, b.top, size.width + size.bleed * 2, b.bottom - b.top);
        } else {
          ctx.save();
          if (dx !== 0) ctx.translate(dx, 0);
          paintLiveLayer(ctx, theme, size, pass.layer, time);
          ctx.restore();
        }
      }
      paintParticles(ctx, theme, size, time);
    }
    if (intensity > 0.01) this.tint(ctx, theme, intensity);
    this.drawFade(ctx, now);
    this.drawn = true;
    return baked;
  }

  /** Release offscreen canvases. The renderer can still be used afterwards. */
  dispose(): void {
    this.invalidate();
    this.fade = null;
  }

  private invalidate(): void {
    if (this.passes) for (const p of this.passes) if (p.kind === 'static') releaseCanvas(p.bitmap.canvas);
    this.passes = null;
  }

  private compile(theme: ThemePack): CompiledPass[] {
    return planPasses(planLayers(theme, true)).map((pass): CompiledPass => (pass.kind === 'live' ? pass : { kind: 'static', depth: pass.depth, bitmap: this.bake(theme, pass.layers) }));
  }

  /** Paint a static pass into an offscreen canvas cropped to the rows it touches. */
  private bake(theme: ThemePack, layers: readonly PlannedLayer[]): Bitmap {
    const { size, ratio } = this;
    let top = size.height;
    let bottom = 0;
    for (const pl of layers) {
      const e = layerExtent(pl.layer, { palette: theme.palette, size, time: 0, seed: pl.seed });
      top = Math.min(top, e.top);
      bottom = Math.max(bottom, e.bottom);
    }
    if (bottom <= top) bottom = top + 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil((size.width + size.bleed * 2) * ratio));
    canvas.height = Math.max(1, Math.ceil((bottom - top) * ratio));
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.setTransform(ratio, 0, 0, ratio, size.bleed * ratio, -top * ratio);
      paintStaticLayers(ctx, theme, size, layers);
    }
    return { canvas, top, bottom };
  }

  /** Copy of what is on screen now, for the crossfade. */
  private snapshot(): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = this.canvas.width;
    c.height = this.canvas.height;
    c.getContext('2d')?.drawImage(this.canvas, 0, 0);
    return c;
  }

  private drawFade(ctx: CanvasRenderingContext2D, now: number): void {
    const fade = this.fade;
    if (!fade) return;
    fade.start ??= now;
    const t = (now - fade.start) / CROSSFADE_MS;
    if (t >= 1) {
      releaseCanvas(fade.snapshot);
      this.fade = null;
      return;
    }
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1 - smoothstep(0, 1, t);
    ctx.drawImage(fade.snapshot, 0, 0, this.canvas.width, this.canvas.height);
    ctx.restore();
  }

  /** A soft warm glow rising from the bottom as the stack gets high — never a harsh flash. */
  private tint(ctx: CanvasRenderingContext2D, theme: ThemePack, intensity: number): void {
    const k = clamp(intensity, 0, 1);
    const { width, height } = this.size;
    const top = height * 0.55;
    const g = ctx.createLinearGradient(0, top, 0, height);
    g.addColorStop(0, rgba(theme.palette.accent, 0));
    g.addColorStop(1, rgba(theme.palette.accent, 0.14 * k));
    ctx.fillStyle = g;
    ctx.fillRect(0, top, width, height - top);
  }
}

/** Free a canvas's backing store now rather than whenever GC gets to it (matters on iOS). */
function releaseCanvas(c: HTMLCanvasElement): void {
  c.width = 0;
  c.height = 0;
}
